import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ExternalLink, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { CompanyPage } from "@/components/radar/CompanyPage";
import { useRadar } from "@/components/radar/Bits";
import { api } from "@/lib/api";
import { RP, rErr } from "@/lib/radar";

export const coKey = (n) => String(n || "").toLowerCase().replace(/[.,]/g, " ").replace(/\b(inc|llc|ltd|corp|corporation|co|company|gmbh|plc)\b/g, "").replace(/\s+/g, " ").trim();
const host = (u) => String(u || "").replace(/^https?:\/\/(www\.)?/, "").split("/")[0].toLowerCase();
const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const match = (items, g) => items.find((c) => (g.domain && c.domain && host(c.domain) === host(g.domain)) || coKey(c.name) === coKey(g.name));

// Live §45 CSV import of signal companies -> {import result}.
export const importSignalCompanies = async (list) => {
  const csv = ["Company,Website,Description", ...list.map((g) => [g.name, g.domain, g.signals.map((s) => s.title || s.description).filter(Boolean).join(" | ")].map(esc).join(","))].join("\n");
  const fd = new FormData();
  fd.append("file", new File([csv], "workflow-signals.csv", { type: "text/csv" }));
  fd.append("mapping", JSON.stringify({ name: "Company", website: "Website", description: "Description" }));
  fd.append("list_name", "Workflow signals");
  return (await api.post("/admin/rcc/radar/import", fd)).data;
};

const resolve = async (g, refresh) => {
  const all = (await api.get("/admin/rcc/radar/companies?limit=1000&sort=added")).data?.items || [];
  const hit = g.id ? { id: g.id } : match(all, g);
  if (hit) return hit.id;
  const r = await importSignalCompanies([g]);
  refresh();
  const mine = (await api.get(`/admin/rcc/radar/companies?import_id=${r.id}&limit=50`)).data?.items || [];
  const c = match(mine, g) || mine[0];
  if (!c) throw new Error("The company was imported but couldn't be found");
  toast.success(`${g.name} added to the radar`);
  return c.id;
};

// Side panel with a Radar company's full info; adds the company first if it isn't in the Radar yet. Prev/next + arrow keys step through `list`.
export const CompanySheet = ({ list = [], index, setIndex, onClose }) => {
  const target = index == null ? null : list[index];
  const { refresh } = useRadar();
  const [id, setId] = useState(null);
  const [err, setErr] = useState("");
  const n = list.length;
  const step = (d) => { if (index == null || !n) return; const i = index + d; if (i >= 0 && i < n) setIndex(i); };
  useEffect(() => {
    if (index == null) return undefined;
    const onKey = (e) => {
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable || [...document.querySelectorAll("[role=dialog]")].some((x) => x.dataset.testid !== "radar-co-sheet")) return;
      if (e.key === "ArrowRight") { e.preventDefault(); step(1); } else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, n]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setId(null); setErr("");
    if (!target) return undefined;
    let live = true;
    resolve(target, refresh).then((x) => live && setId(x)).catch((e) => live && setErr(rErr(e)));
    return () => { live = false; };
  }, [target?.name, target?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const navBtn = "rounded-full border border-[#27272A] p-1.5 text-zinc-300 hover:border-[#D4AF37]/50 hover:text-[#F3C649] disabled:opacity-30 disabled:hover:border-[#27272A] disabled:hover:text-zinc-300";
  return (
    <Sheet open={!!target} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto border-[#27272A] bg-[#09090B] sm:max-w-2xl" data-testid="radar-co-sheet">
        <SheetHeader className="mb-3 flex-row flex-wrap items-center gap-2 space-y-0 pr-8">
          {n > 1 && <div className="flex items-center gap-1.5" data-testid="radar-co-sheet-nav">
            <button onClick={() => step(-1)} disabled={index === 0} className={navBtn} title="Previous (←)" data-testid="radar-co-sheet-prev"><ChevronLeft className="h-4 w-4" /></button>
            <span className="text-[11px] tabular-nums text-zinc-500" data-testid="radar-co-sheet-pos">{index + 1} of {n}</span>
            <button onClick={() => step(1)} disabled={index === n - 1} className={navBtn} title="Next (→)" data-testid="radar-co-sheet-next"><ChevronRight className="h-4 w-4" /></button>
          </div>}
          <SheetTitle className="min-w-0 flex-1 truncate text-zinc-50" data-testid="radar-co-sheet-title">{target?.name}</SheetTitle>
          {id && <Link to={`${RP}/companies/${id}`} onClick={onClose} className="flex items-center gap-1 text-[11px] text-[#D4AF37] hover:underline" data-testid="radar-co-sheet-full"><ExternalLink className="h-3 w-3" />Open full page</Link>}
        </SheetHeader>
        {err ? <p className="text-sm text-red-300" data-testid="radar-co-sheet-error">{err}</p>
          : !id ? <p className="flex items-center gap-2 text-sm text-zinc-500" data-testid="radar-co-sheet-loading"><Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" />Opening {target?.name}…</p>
          : <CompanyPage id={id} embedded />}
      </SheetContent>
    </Sheet>
  );
};
